import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePlan from '../../hooks/usePlan';
import {
  Link2, ExternalLink, RefreshCw, CheckCircle, Clock, Send, AlertCircle,
  Globe, TrendingUp, Award, Plus, Crown
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  queued: { label: 'In wachtrij', color: '#64748b', bg: '#f1f5f9', icon: Clock },
  outreach_queued: { label: 'Outreach klaar', color: '#0ea5e9', bg: '#e0f2fe', icon: Send },
  sent: { label: 'Verstuurd', color: '#D4A017', bg: '#fef9e7', icon: Send },
  live: { label: 'LIVE ✓', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  declined: { label: 'Afgewezen', color: '#dc2626', bg: '#fee2e2', icon: AlertCircle },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

export default function BacklinksPage() {
  const { api } = useAuth();
  const plan = usePlan();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queueing, setQueueing] = useState(false);

  const load = async () => {
    try {
      const r = await api('get', '/api/platform/backlinks');
      setData(r.data);
    } catch (e) {
      toast.error('Kon backlinks niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQueue = async () => {
    if (!plan.isManaged) {
      toast.info('Backlinks zijn onderdeel van het Managed AI pakket.');
      return;
    }
    setQueueing(true);
    try {
      const r = await api('post', '/api/platform/backlinks/queue', { max: 5 });
      if (r.data.queued > 0) {
        toast.success(`${r.data.queued} nieuwe backlink-aanvragen toegevoegd`);
        load();
      } else {
        toast.info(r.data.reason === 'no_website' ? 'Voeg eerst uw website-URL toe' : 'Alle beschikbare directories zijn al gequeued');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Fout bij queue';
      toast.error(msg);
    } finally {
      setQueueing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  const backlinks = data?.backlinks || [];
  const stats = data?.stats || {};
  const directories = data?.directories || [];
  const isManaged = data?.is_managed;

  return (
    <div data-testid="backlinks-page" style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
            <Link2 size={22} style={{ display: 'inline', verticalAlign: '-3px', color: '#D4A017', marginRight: '8px' }} />
            Backlinks
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            Echte aanvragen bij NL bedrijfsdirectories — getraceerde status, geverifieerde live-links
          </p>
        </div>
        {isManaged && (
          <button
            data-testid="backlinks-queue-btn"
            onClick={handleQueue}
            disabled={queueing}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: '#0f172a', color: 'white', border: 'none', borderRadius: '10px',
              padding: '10px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              opacity: queueing ? 0.6 : 1,
            }}
          >
            <Plus size={14} /> {queueing ? 'Bezig...' : 'Nieuwe batch (max 5)'}
          </button>
        )}
      </div>

      {/* Not managed banner */}
      {!isManaged && (
        <div data-testid="backlinks-upgrade-banner" style={{
          background: 'linear-gradient(135deg, #0f172a, #1e1b4b)',
          borderRadius: '14px', padding: '20px 24px', color: 'white', marginBottom: '20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px',
        }}>
          <div style={{ flex: 1 }}>
            <p style={{ color: '#D4A017', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
              Managed AI · backlinks
            </p>
            <h3 style={{ fontWeight: 800, fontSize: '16px', fontFamily: "'Sora', sans-serif", marginBottom: '4px' }}>
              Backlinks bouwen wordt automatisch geregeld in het Managed AI pakket
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', lineHeight: 1.5 }}>
              Wij sturen écht aanvragen naar {directories.length} NL bedrijfsdirectories en verifiëren elke link.
            </p>
          </div>
          <Link to="/platform/checkout?plan=imperium_beheer" style={{
            background: '#D4A017', color: '#0f172a', padding: '10px 18px', borderRadius: '10px',
            textDecoration: 'none', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap',
          }}>
            <Crown size={13} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '4px' }} /> Upgrade
          </Link>
        </div>
      )}

      {/* Stats — alleen voor managed */}
      {isManaged && (
        <div data-testid="backlinks-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          {[
            { label: 'Totaal', value: stats.total || 0, color: '#0f172a', icon: TrendingUp },
            { label: 'In wachtrij', value: stats.queued || 0, color: '#64748b', icon: Clock },
            { label: 'Verstuurd', value: stats.sent || 0, color: '#D4A017', icon: Send },
            { label: 'LIVE ✓', value: stats.live || 0, color: '#16a34a', icon: CheckCircle },
            { label: 'Beschikbare directories', value: data.directories_available || 0, color: '#7c3aed', icon: Globe },
          ].map(s => {
            const Icon = s.icon;
            return (
              <div key={s.label} style={{
                background: 'white', borderRadius: '12px', padding: '14px',
                border: '1px solid #f1f5f9', textAlign: 'left',
              }}>
                <Icon size={14} style={{ color: s.color, marginBottom: '6px' }} />
                <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: s.color, margin: 0, lineHeight: 1 }}>
                  {s.value}
                </p>
                <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0' }}>{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Backlinks list */}
      {backlinks.length === 0 ? (
        <div data-testid="backlinks-empty" style={{ background: 'white', borderRadius: '14px', padding: '40px 20px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
          <Link2 size={32} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
          <p style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
            Nog geen backlink-aanvragen
          </p>
          <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '14px' }}>
            {isManaged
              ? 'Klik op "Nieuwe batch" om de eerste 5 directories aan te vragen.'
              : 'Upgrade naar Managed AI om automatisch backlinks te laten bouwen.'}
          </p>
          {isManaged && (
            <button
              onClick={handleQueue}
              disabled={queueing}
              style={{
                background: '#D4A017', color: '#0f172a', border: 'none', borderRadius: '10px',
                padding: '10px 18px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              <Plus size={13} style={{ display: 'inline', verticalAlign: '-2px' }} /> Start eerste batch
            </button>
          )}
        </div>
      ) : (
        <div data-testid="backlinks-list" style={{ background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
              {backlinks.length} aanvragen — naar {data.directories_used} verschillende directories
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Directory</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Methode</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>DA</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Aangevraagd</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Live URL</th>
                </tr>
              </thead>
              <tbody>
                {backlinks.map((b, idx) => {
                  const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.queued;
                  const Icon = sc.icon;
                  return (
                    <tr key={b.id} data-testid={`backlink-row-${b.directory_id}`} style={{ borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none' }}>
                      <td style={{ padding: '14px', fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                        {b.directory_name}
                        <p style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 400, marginTop: '2px' }}>{b.directory_domain}</p>
                      </td>
                      <td style={{ padding: '14px', fontSize: '12px', color: '#64748b' }}>
                        {b.submit_method === 'email' ? '📧 Email' : '📝 Formulier'}
                      </td>
                      <td style={{ padding: '14px', fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                        {b.est_da ? <span style={{ background: '#fef9e7', color: '#92400e', padding: '2px 8px', borderRadius: '6px' }}>{b.est_da}</span> : '—'}
                      </td>
                      <td style={{ padding: '14px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          background: sc.bg, color: sc.color, fontSize: '11px', fontWeight: 700,
                          padding: '3px 8px', borderRadius: '6px',
                        }}>
                          <Icon size={11} />
                          {sc.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px', fontSize: '12px', color: '#64748b' }}>
                        {fmtDate(b.created_at)}
                      </td>
                      <td style={{ padding: '14px', fontSize: '12px' }}>
                        {b.status === 'live' && b.live_url ? (
                          <a href={b.live_url} target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Bekijk <ExternalLink size={10} />
                          </a>
                        ) : b.submit_method === 'form' && b.submit_url ? (
                          <a href={b.submit_url} target="_blank" rel="noopener noreferrer" style={{ color: '#64748b', fontSize: '11px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Formulier <ExternalLink size={9} />
                          </a>
                        ) : (
                          <span style={{ color: '#cbd5e1' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer disclaimer — eerlijk over hoe het werkt */}
      <div style={{ marginTop: '20px', padding: '14px 18px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <p style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
          <strong style={{ color: '#0f172a' }}>Hoe werken backlinks?</strong> Wij sturen écht aanvragen naar de directories.
          Voor email-method directories versturen wij een outreach-email vanuit onze platform met uw bedrijfsgegevens.
          Voor formulier-method directories doet ons team handmatig de submission.
          Status "LIVE ✓" betekent dat onze verifier (elke 6u) heeft gedetecteerd dat uw link daadwerkelijk op de directory staat.
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
